import { Modal } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import {
  MdOutlinePayments,
  MdContentCopy,
  MdCheckCircle,
  MdOutlineRefresh,
  MdInfoOutline,
  MdClose,
} from "react-icons/md";
import { useForm } from "react-hook-form";
import axios from "../../config/axiosInstance";
import { PAYMENTS } from "../../config/apiConfig";
import { useEffect, useMemo, useState } from "react";
import { useApplication } from "../../contexts/ApplicationContext";
import { formatToReal } from "../../utils/utils";
import CurrencyInput from "../inputs/CurrencyInput";

const STEPS = ["Valor", "Pagamento", "Confirmação"];

const Stepper = ({ current }) => (
  <div className="flex items-center">
    {STEPS.map((label, idx) => {
      const stepNumber = idx + 1;
      const isDone = stepNumber < current;
      const isActive = stepNumber === current;
      return (
        <div key={label} className="flex flex-1 items-center last:flex-none">
          <div className="flex items-center gap-2">
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
                isDone
                  ? "bg-emerald-100 text-emerald-700"
                  : isActive
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-400"
              }`}
            >
              {isDone ? "✓" : stepNumber}
            </span>
            <span
              className={`whitespace-nowrap text-[11px] font-semibold ${
                isActive ? "text-slate-800" : "text-slate-400"
              }`}
            >
              {label}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <span
              className={`mx-3 h-px flex-1 ${
                isDone ? "bg-emerald-200" : "bg-slate-200"
              }`}
            />
          )}
        </div>
      );
    })}
  </div>
);

const InfoNote = () => (
  <div className="flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50/70 px-3.5 py-3 text-blue-900">
    <MdInfoOutline className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
    <p className="text-xs leading-relaxed">
      Após efetuar o pagamento, o saldo pode levar{" "}
      <strong>alguns minutos</strong> para aparecer na sua conta. Você receberá
      a confirmação dentro do sistema.
    </p>
  </div>
);

const ModalAddCredit = ({ isOpen, setOpenModal }) => {
  const { user } = useApplication();
  const [qrCode, setQrCode] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [errorPayment, setErrorPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const minRecharge = useMemo(
    () => Number(user?.Company?.minRechargePRE || 0),
    [user],
  );

  const suggestedValues = useMemo(() => {
    const base = [50, 100, 250, 500].filter((v) => v >= minRecharge);
    if (minRecharge > 0 && !base.includes(minRecharge)) {
      base.unshift(minRecharge);
    }
    return [...new Set(base)].slice(0, 4);
  }, [minRecharge]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    control,
    formState: { errors, isSubmitting },
    clearErrors,
    watch,
  } = useForm();

  const valuePix = watch("valuePix");

  const handlePixPayment = async (data) => {
    try {
      const value = Number(data.valuePix || 0);
      if (value < minRecharge) {
        setError("valuePix", {
          type: "manual",
          message: `O valor mínimo permitido é ${minRecharge} reais`,
        });
        return;
      }
      setErrorPayment(null);
      setIsLoading(true);

      const { data: resp } = await axios.get(
        `${PAYMENTS.CREATE}?value=${value}&type=PIX`,
      );

      // Compatível com Asaas e MercadoPago (mock)
      setQrCode(
        resp?.data?.encodedImage ||
          resp?.data?.qr_code_base64?.replace(/^data:image\/png;base64,/, "") ||
          resp?.data?.qr_code ||
          "",
      );
      setPaymentLink(
        resp?.data?.payload || resp?.data?.qr_code || resp?.data?.link || "",
      );
      setCopied(false);
    } catch (error) {
      setErrorPayment(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(paymentLink || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // silencioso
    }
  };

  const handleGenerateAgain = () => {
    // permite gerar um novo pagamento mantendo o valor atual
    if (!valuePix || Number(valuePix) < minRecharge) return;
    handlePixPayment({ valuePix });
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    reset();
    setErrorPayment(null);
    setPaymentLink("");
    setQrCode("");
    setCopied(false);
    // define valor mínimo por padrão (opcional)
    if (minRecharge > 0) setValue("valuePix", minRecharge);
  }, [isOpen, reset, setValue, minRecharge]);

  const showForm = !qrCode;
  const showPayment = !!qrCode;

  return (
    <Modal show={isOpen} size="xl" onClose={handleClose} popup>
      <Modal.Body className="!p-0">
        <div className="overflow-hidden rounded-lg">
          {/* ── Banner ── */}
          <div
            className="relative overflow-hidden px-6 pb-5 pt-6 text-white"
            style={{
              background: "var(--hero-gradient)",
            }}
          >
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 90% 0%, var(--hero-glow), transparent 50%)",
              }}
            />
            <button
              type="button"
              onClick={handleClose}
              aria-label="Fechar"
              className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-white/50 transition hover:bg-white/10 hover:text-white"
              style={{ zIndex: 50 }}
            >
              <MdClose size={18} />
            </button>

            <div className="relative z-10 flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10">
                <MdOutlinePayments size={22} />
              </div>
              <div>
                <h3 className="text-lg font-bold leading-tight">
                  Adicionar créditos
                </h3>
                <p className="text-xs text-blue-100/70">
                  Pague via PIX e o saldo entra automaticamente
                </p>
              </div>
            </div>

            <div className="relative z-10 mt-4 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-100/60">
                Saldo atual
              </span>
              <span className="text-sm font-bold tabular-nums">
                {formatToReal(user?.Company?.credit || 0)}
              </span>
            </div>
          </div>

          {/* ── Conteúdo ── */}
          <div className="space-y-5 px-6 py-5">
            <Stepper current={showPayment ? 2 : 1} />

            {showForm && (
              <form
                onSubmit={handleSubmit(handlePixPayment)}
                className="space-y-4"
              >
                {suggestedValues.length > 0 && (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-slate-500">
                      Sugestões rápidas
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {suggestedValues.map((v) => {
                        const isSelected = Number(valuePix) === v;
                        return (
                          <button
                            type="button"
                            key={v}
                            onClick={() => {
                              setValue("valuePix", v);
                              clearErrors("valuePix");
                            }}
                            className={`rounded-full border px-4 py-1.5 text-[13px] font-semibold transition-colors ${
                              isSelected
                                ? "border-blue-600 bg-blue-600 text-white"
                                : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"
                            }`}
                          >
                            {formatToReal(v)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <CurrencyInput
                  control={control}
                  name="valuePix"
                  register={register}
                  label="Valor da recarga (R$)"
                  required
                  min={minRecharge}
                />

                {errors.valuePix ? (
                  <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
                    {errors.valuePix?.message ||
                      `Valor mínimo de ${minRecharge} reais.`}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">
                    Valor mínimo de{" "}
                    <span className="font-semibold text-slate-600">
                      {formatToReal(minRecharge)}
                    </span>
                    .
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isLoading || isSubmitting}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 py-3 text-sm font-bold text-white shadow-md transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoading || isSubmitting ? (
                    <AiOutlineLoading className="h-4 w-4 animate-spin" />
                  ) : (
                    <MdOutlinePayments size={17} />
                  )}
                  Gerar pagamento PIX
                </button>

                <InfoNote />
              </form>
            )}

            {showPayment && (
              <div className="space-y-4">
                {/* Resumo do valor */}
                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      Valor da recarga
                    </p>
                    <p className="text-xl font-bold tabular-nums text-slate-800">
                      {formatToReal(Number(valuePix) || 0)}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-[11px] font-bold text-teal-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
                    PIX
                  </span>
                </div>

                {/* QR Code */}
                <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center shadow-sm">
                  <p className="text-xs font-semibold text-slate-500">
                    Escaneie o QR Code com o app do seu banco
                  </p>
                  {qrCode ? (
                    <img
                      className="mx-auto mt-3 h-52 w-52 rounded-lg object-contain"
                      alt="QR Code PIX"
                      src={`data:image/png;base64,${qrCode}`}
                    />
                  ) : (
                    <div className="mx-auto mt-3 flex h-52 w-52 items-center justify-center">
                      <AiOutlineLoading className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                    ou copie o código
                  </span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>

                {/* Código PIX (copiar) */}
                <div className="flex items-stretch gap-2">
                  <div className="flex-1 overflow-x-auto whitespace-nowrap rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-xs text-slate-600">
                    {paymentLink}
                  </div>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 text-xs font-bold transition-colors ${
                      copied
                        ? "bg-emerald-600 text-white"
                        : "border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"
                    }`}
                  >
                    {copied ? (
                      <>
                        <MdCheckCircle size={15} />
                        Copiado
                      </>
                    ) : (
                      <>
                        <MdContentCopy size={15} />
                        Copiar
                      </>
                    )}
                  </button>
                </div>

                <InfoNote />

                {/* Ações */}
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleGenerateAgain}
                    disabled={isLoading}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 transition hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <MdOutlineRefresh size={15} />
                    Gerar novamente
                  </button>
                  <button
                    type="button"
                    onClick={() => setQrCode("")}
                    className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 transition hover:border-blue-300 hover:text-blue-700"
                  >
                    Escolher outro valor
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 px-5 py-2.5 text-xs font-bold text-white shadow-md transition hover:brightness-110 sm:ml-auto"
                  >
                    Concluir
                  </button>
                </div>
              </div>
            )}

            {errorPayment && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                <span className="font-semibold">Ops!</span> Não foi possível
                gerar a cobrança por PIX agora. Tente novamente em instantes.
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ModalAddCredit;
